import React, { createContext, useContext, useEffect, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight, ExternalLink } from 'lucide-react';

// ══════════════════════════════════════════════════════════════════════
// Global photo viewer — lives OUTSIDE App.jsx on purpose.
//
// Before this fix, `modalPhoto` was state inside App.jsx (a ~4,900-line
// component with 90+ useState hooks and zero memoized children). Opening a
// photo from Daily Activities / Photos / Team / Tasks called `setModalPhoto`,
// which is App's OWN state — so every single open/close forced App to
// re-render its ENTIRE tree (header, sidebar, and whichever heavy view is
// currently mounted — ScheduleView, TeamView, etc. can run 1,000+ lines).
// That full-tree re-render landing on the exact same frame the modal needed
// to animate in/out is what showed up as a "flicker"/render glitch on open
// and close.
//
// Moving the state up into this small Provider (rendered ABOVE <App/> in
// src/index.tsx) fixes it structurally: when a photo opens/closes, only
// THIS component re-renders. The `children` it renders (<App/>) is the same
// element reference every time, since whoever renders <PhotoModalProvider>
// isn't re-rendering just because a photo opened — so React bails out of
// re-rendering <App/> and everything inside it entirely. No prop needs to
// be memoized anywhere else for this to work.
//
// IMPORTANT: the context's value is the `setModalPhoto` SETTER ALONE, not an
// object like `{ modalPhoto, setModalPhoto }`. A setState setter is itself
// referentially stable forever (React guarantees this) — but bundling it
// into a fresh `{ ... }` object on every render would hand consumers (App.jsx
// included) a NEW context value every time `modalPhoto` changes, which would
// force them to re-render anyway and silently undo this whole fix. Exposing
// the bare setter keeps the context value identical across renders, so
// App.jsx (which only ever needs to call the setter, never read the current
// photo) truly never re-renders because of this context.
// ══════════════════════════════════════════════════════════════════════

const PhotoModalSetterContext = createContext(null);

export function usePhotoModal() {
    const setModalPhoto = useContext(PhotoModalSetterContext);
    if (!setModalPhoto) throw new Error('usePhotoModal must be used within a PhotoModalProvider');
    return setModalPhoto;
}

export function PhotoModalProvider({ children }) {
    const [modalPhoto, setModalPhoto] = useState(null);

    // ✅ رمشة الصورة نفسها (مش التعارض مع رندر App — ده كان اتحل قبل كده):
    // الصورة كانت بتدخل فجأة (pop) لحظة ما تخلص تحميل/decode من الشبكة،
    // مش بنعومة زي الخلفية والمودال اللي بيعملوا fade-in بـ opacity. في صور
    // الأنشطة اليومية وقسم الصور بالذات الصور دي أكبر حجمًا (مش سيلفي حضور
    // صغير أو صورة تحدي واحدة)، فبتاخد وقت تحميل ملحوظ أكتر، فالفرق بين
    // "المودال خلص يفتح" و"الصورة لسة واصلة" بيبان كأنه رمشة/فلاش مفاجئ
    // للصورة بالذات. الحل: نتتبّع لحظة ما الصورة (أو صورة الخطأ البديلة)
    // فعليًا خلصت تحميل، ونخلي لها fade-in مستقل بتاعها (opacity transition)
    // بدل ما تظهر فجأة. imageReady بيترجع false تلقائيًا كل ما الـ image_url
    // يتغيّر (فتح صورة جديدة، أو next/prev، أو تنقل من الشريط تحت).
    const [imageReady, setImageReady] = useState(false);
    useEffect(() => {
        // ✅ الشرط ده (if modalPhoto) مهم: من غيره، لما المودال يتقفل
        // (modalPhoto بيبقى null)، الـ dependency (modalPhoto?.image_url)
        // برضو بيتغيّر (من رابط الصورة لـ undefined)، فالـ effect كان بيشتغل
        // تاني ويعمل setImageReady(false) في نفس اللحظة اللي حركة الإغلاق
        // (AnimatePresence exit) شغالة فيها — رندر إضافي زيادة مالوش داعي
        // بيحصل بالظبط وسط حركة القفل، وده اللي كان بيسبب فلاشة/رجفة سريعة
        // (المودال يرجع يبان كامل للحظة واحدة) قبل ما يقفل فعلاً. دلوقتي
        // الـ effect مابيشتغلش إلا لو فيه صورة جديدة فعلاً بتتفتح.
        if (modalPhoto) setImageReady(false);
    }, [modalPhoto?.image_url]);

    return (
        <PhotoModalSetterContext.Provider value={setModalPhoto}>
            {children}

            {/* ══════ GLOBAL PHOTO MODAL ══════ (moved verbatim from App.jsx) */}
            {/* ✅ لسة في رمشة هنا بالذات (مش في المودالز التانية) لأن ده الوحيد
                اللي فعليًا بيحمّل صورة حقيقية تقيلة من الشبكة. كان فيه حركتين
                مع بعض على نفس العنصر اللي جواه الصورة: opacity + scale (0.9→1).
                الـ scale transform بياخد وقت processing أعلى من opacity البسيطة،
                وده بالذات بيتعارض مع لحظة الصورة الكبيرة بتتعمل لها decode —
                الفريم اللي بيحصل فيه الاتنين مع بعض (scale بيتغيّر + decode
                بيخلص) هو اللي بيبان "رمشة". شلنا الـ scale وسيبنا بس opacity
                (أرخص حاجة ممكنة للمتصفح يحركها، مفيش أي تعارض مع تحميل الصورة)
                + ضفنا decoding="async" على الصورة عشان المتصفح يعمل الـ decode
                في الخلفية بدل ما يوقف الرسم لحدي ما يخلص. */}
            {/* ✅ تأكّدنا بالـ DevTools (MutationObserver فعلي على الصفحة الحية في
                البرودكشن) إن العنصر ده بيتحط في الـ DOM مرة واحدة بالظبط
                ويتشال مرة واحدة بالظبط — مفيش نسخة مكررة ولا تبديل زيادة في
                React نفسه. يبقى الفلاشة مش باگ state — هي حصلت في الفترة
                اللي بين "اتقال اتشال" و"فعليًا اتشال من الـ DOM"، وهي الفترة
                اللي حركة الـ exit (opacity fade) كانت شغالة فيها، ولسبب مش
                قادرين نتحكم فيه من كود React (تزاحم على main thread وقت ما
                الصفحة بتاخد render تقيل للستة قوائم اللي تحتها، تراكب frames
                من المتصفح، إلخ) كان بيبان فلاش كامل للمودال قبل ما يختفي
                فعليًا. الحل القاطع: شلنا حركة الإغلاق (exit) خالص — المودال
                دلوقتي بيختفي فورًا بمجرد ما تقفله (زي ما كان أصلاً بيفتح على
                طول)، فمفيش "فترة انتقال" خالص ممكن الفلاشة تحصل فيها. */}
            {/* ✅ نفس المنطق ده بالظبط بيتكرر على الفتح: شلنا initial/animate
                (fade-in بالـ opacity) من الاتنين (الخلفية + المحتوى) كمان —
                المودال دلوقتي بيظهر فورًا بمجرد ما يتفتح، بالظبط زي ما بيختفي
                فورًا بمجرد ما يتقفل. مفيش "فترة انتقال" خالص فاضلة (مش فتح
                ولا قفل) ممكن الفلاشة/الرمشة تحصل فيها. */}
            <AnimatePresence>
                {modalPhoto && (
                    <motion.div key="photo-modal-backdrop"
                        className="fixed inset-0 bg-black/90 flex items-center justify-center z-[99999] p-4"
                        onClick={() => setModalPhoto(null)}
                        tabIndex={0} style={{ outline: 'none' }}>
                        <motion.div
                            className="relative w-full max-w-5xl max-h-[95vh] flex flex-col rounded-2xl overflow-hidden"
                            onClick={e => e.stopPropagation()}>

                            {/* Close */}
                            <button onClick={() => setModalPhoto(null)}
                                className="absolute top-4 right-4 z-10 p-2 bg-black/50 rounded-xl hover:bg-black/70">
                                <X size={20} className="text-white" />
                            </button>

                            {/* ← Prev */}
                            {modalPhoto.allImages?.length > 1 && (
                                <button onClick={() => {
                                    const prev = (modalPhoto.imageIndex - 1 + modalPhoto.allImages.length) % modalPhoto.allImages.length;
                                    setModalPhoto({ ...modalPhoto, image_url: modalPhoto.allImages[prev], imageIndex: prev });
                                }} className="absolute left-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/50 rounded-xl hover:bg-black/70">
                                    <ChevronLeft size={24} className="text-white" />
                                </button>
                            )}

                            {/* Next → */}
                            {modalPhoto.allImages?.length > 1 && (
                                <button onClick={() => {
                                    const next = (modalPhoto.imageIndex + 1) % modalPhoto.allImages.length;
                                    setModalPhoto({ ...modalPhoto, image_url: modalPhoto.allImages[next], imageIndex: next });
                                }} className="absolute right-4 top-1/2 -translate-y-1/2 z-10 p-3 bg-black/50 rounded-xl hover:bg-black/70">
                                    <ChevronRight size={24} className="text-white" />
                                </button>
                            )}

                            {/* Image
                                ✅ كانت maxHeight بس (سقف لا قاعدة) — يعني الصندوق ده كان بلا
                                ارتفاع محدد قبل ما الصورة توصل، وبيتمدد/يتقفز فجأة لحجمه النهائي
                                لحظة ما الصورة تخلص تحميل من الشبكة (غالبًا بعد ما حركة فتح المودل
                                نفسها خلصت) — القفزة دي هي اللي بتحس بيها "رمشة"، وبتبان أوضح في
                                المودلز اللي فيها صورة بالذات (سكشن الصور / فيو صور الديلي اكتفتي)
                                لأن تحميل الصورة ممكن ياخد وقت أطول من حركة الفتح (~0.3 ثانية).
                                الحل: ارتفاع ثابت (height) بدل سقف (maxHeight) — الصندوق بياخد
                                حجمه النهائي فورًا من أول رندر، قبل ما الصورة توصل خالص، فما
                                فيش قفزة لما تحمّل. */}
                            <div className="bg-black flex items-center justify-center" style={{ height: '70vh' }}>
                                <img src={modalPhoto.image_url} alt={modalPhoto.title || ''}
                                    decoding="async" loading="eager"
                                    onLoad={() => setImageReady(true)}
                                    onError={e => { e.target.src = 'https://via.placeholder.com/800x600?text=Not+Found'; setImageReady(true); }}
                                    className="w-full h-full object-contain"
                                    style={{ opacity: imageReady ? 1 : 0, transition: 'opacity 0.2s ease' }} />
                            </div>

                            {/* Footer */}
                            <div className="bg-gray-900 px-6 py-4 flex items-center justify-between gap-4">
                                <div className="flex-1 min-w-0">
                                    <h4 className="font-black text-white text-lg truncate">{modalPhoto.title || modalPhoto.system || 'Photo'}</h4>
                                    <p className="text-gray-400 text-sm truncate">{modalPhoto.description || ''}</p>
                                    {modalPhoto.uploaded_by && (
                                        <p className="text-gray-500 text-xs mt-1">{modalPhoto.uploaded_by} • {new Date(modalPhoto.uploaded_at).toLocaleString()}</p>
                                    )}
                                </div>
                                <div className="flex items-center gap-3 flex-shrink-0">
                                    {modalPhoto.allImages?.length > 1 && (
                                        <span className="text-gray-400 text-sm font-bold">
                                            {modalPhoto.imageIndex + 1} / {modalPhoto.allImages.length}
                                        </span>
                                    )}
                                    <a href={modalPhoto.image_url} target="_blank" rel="noopener noreferrer"
                                        className="px-4 py-2 bg-pink-600 text-white rounded-xl font-bold flex items-center gap-2 hover:bg-pink-700 text-sm">
                                        <ExternalLink size={14} />Open
                                    </a>
                                </div>
                            </div>

                            {/* Thumbnail strip */}
                            {modalPhoto.allImages?.length > 1 && (
                                <div className="bg-gray-950 px-4 py-3 flex gap-2 overflow-x-auto">
                                    {modalPhoto.allImages.map((img, idx) => (
                                        <img key={idx} src={img} alt="" decoding="async"
                                            onClick={() => setModalPhoto({ ...modalPhoto, image_url: img, imageIndex: idx })}
                                            className={`w-12 h-12 rounded-lg object-cover flex-shrink-0 cursor-pointer transition-all ${idx === modalPhoto.imageIndex ? 'ring-2 ring-pink-500 opacity-100' : 'opacity-50 hover:opacity-80'
                                                }`}
                                            onError={e => { e.target.style.display = 'none'; }} />
                                    ))}
                                </div>
                            )}
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </PhotoModalSetterContext.Provider>
    );
}
